/**
 * SavedDays.js
 *
 * Copyright (c) 2026 Eric Seigne <eric.seigne@cap-rel.fr>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

import { debugSavedPlans } from './debug.js'
import { Store } from '../store/Store.js'
import { SavedPlans, APP_VERSION } from './SavedPlans.js'
import { chainedPlan } from '../core/DayPlanner.js'
import { PLAN_ERROR_CODES } from '../core/errors.js'
import { Filesystem, Directory } from '@capacitor/filesystem'
import { Share } from '@capacitor/share'

function isNativePlatform() {
  return typeof window !== 'undefined' && window.Capacitor?.isNativePlatform?.()
}

// Named days of diving, kept beside the saved plans and never mixed with them.
//
// A separate list, and not a `type` field inside mvplan_saved_plans: every
// reader of that list (SavedPlans.validateForLoad, extractSummary, the plan
// screen) expects a single dive with its segments, and a day would either be
// refused or crash them. Two lists cost one storage key; one list would cost a
// rewrite of everything that already works.
const SAVED_DAYS_KEY = 'mvplan_saved_days'

// Marker written in an exported file, and required when one is imported: a
// plan and a day do not describe the same thing, and reading one for the other
// would silently produce an empty series.
const DAY_FILE_TYPE = 'mvplan-day'

export class SavedDays {
  // What a saved day carries of a dive: its inputs, never its computed plan.
  //
  // Same rule as store.state.day: a plan read back from storage was computed
  // with settings that may have changed since, and nothing on screen would say
  // so. A loaded day is a day to compute again.
  static diveInputs(dive) {
    return {
      id: dive.id,
      label: dive.label || '',
      startTime: dive.startTime || '',
      surfaceIntervalBefore: dive.surfaceIntervalBefore ?? null,
      segments: dive.segments,
      ccr: dive.ccr || null
    }
  }

  // Identifier of a new entry.
  //
  // Date.now() alone is not enough: saving a day and duplicating it right away
  // happens within the same millisecond, both entries then carry the same id,
  // and delete(id) removes the two of them. The first free value at or after
  // the current time is used instead, which keeps the ids sortable by date.
  static nextId(days) {
    const used = new Set(days.map(day => day.id))
    let id = Date.now()
    while (used.has(id)) id++
    return id
  }

  static getAll() {
    try {
      const saved = localStorage.getItem(SAVED_DAYS_KEY)
      const days = saved ? JSON.parse(saved) : []
      if (!Array.isArray(days)) {
        console.error('SavedDays.getAll: the stored list is not an array (%o), an empty list is returned', days)
        return []
      }
      debugSavedPlans('SavedDays.getAll: loaded %d days', days.length)
      return days
    } catch (e) {
      console.warn('SavedDays.getAll: failed to load the saved days:', e)
      return []
    }
  }

  static saveAll(days) {
    try {
      localStorage.setItem(SAVED_DAYS_KEY, JSON.stringify(days))
      return true
    } catch (e) {
      console.error('SavedDays.saveAll: failed to store %d day(s):', days.length, e)
      return false
    }
  }

  /**
   * Save the day currently held by the state under a name.
   *
   * The shared inputs travel with it - gases, diluents, settings, environment -
   * for the same reason a saved plan carries them: reloading a series while
   * the application is set on another site, other mixes or other gradient
   * factors would recompute a day that is not the one that was saved.
   *
   * Returns the id of the saved day, or null when there was nothing to save.
   */
  static save(name, state) {
    const dives = state?.day?.dives
    if (!Array.isArray(dives) || dives.length === 0) {
      console.error('SavedDays.save: the day carries no dive (%o), nothing saved', dives)
      return null
    }

    let environment
    if (state.environment && typeof state.environment === 'object' && !Array.isArray(state.environment)) {
      environment = { ...state.environment }
    } else {
      console.warn('SavedDays.save: the state carries no environment (%o), the day is saved at sea level in sea water',
        state.environment)
      environment = { ...SavedPlans.LEGACY_ENVIRONMENT }
    }

    const days = SavedDays.getAll()
    const day = {
      id: SavedDays.nextId(days),
      name: name || SavedDays.generateName(state),
      createdAt: new Date().toISOString(),
      dives: dives.map(SavedDays.diveInputs),
      gases: state.gases,
      diluents: state.diluents,
      settings: state.settings,
      environment,
      summary: SavedDays.extractSummary(state)
    }

    days.unshift(day)
    if (!SavedDays.saveAll(days)) return null

    debugSavedPlans('SavedDays.save: saved day id=%d name="%s" (%d dives)', day.id, day.name, day.dives.length)
    return day.id
  }

  static generateName(state) {
    const dives = state?.day?.dives || []
    const deepest = dives.reduce(
      (max, dive) => Math.max(max, ...dive.segments.map(s => s.depth || 0)), 0)
    const date = new Date().toLocaleDateString()
    return `${dives.length} x ${deepest}m - ${date}`
  }

  /**
   * What the list of saved days displays without recomputing anything.
   *
   * The totals come from the plans the day carried when it was saved; a day
   * saved before being computed carries none, and the fields are then null
   * rather than zero - the list shows nothing instead of showing a day with no
   * decompression.
   */
  static extractSummary(state) {
    const dives = state?.day?.dives || []
    const computed = dives.map(dive => chainedPlan(dive.results)).filter(Boolean)
    const last = computed.length > 0 ? computed[computed.length - 1] : null

    return {
      diveCount: dives.length,
      computedCount: computed.length,
      maxDepth: dives.reduce((max, dive) => Math.max(max, ...dive.segments.map(s => s.depth || 0)), 0),
      totalBottomTime: dives.reduce(
        (sum, dive) => sum + dive.segments.reduce((t, s) => t + (s.time || 0), 0), 0),
      cnsTotal: last && Number.isFinite(last.cnsTotal) ? last.cnsTotal : null,
      otuTotal: last && Number.isFinite(last.otuTotal) ? last.otuTotal : null,
      noFlyTime: last && Number.isFinite(last.noFlyTime) ? last.noFlyTime : null,
      gf: state?.settings
        ? `${Math.round(state.settings.gfLow * 100)}/${Math.round(state.settings.gfHigh * 100)}`
        : null
    }
  }

  static get(id) {
    return SavedDays.getAll().find(day => day.id === id)
  }

  static rename(id, newName) {
    const days = SavedDays.getAll()
    const day = days.find(d => d.id === id)
    if (!day) {
      console.error('SavedDays.rename: day id=%o not found, nothing renamed', id)
      return false
    }
    if (typeof newName !== 'string' || newName.trim() === '') {
      console.error('SavedDays.rename: day id=%o given an empty name (%o), the previous one is kept', id, newName)
      return false
    }
    day.name = newName.trim()
    return SavedDays.saveAll(days)
  }

  static delete(id) {
    const days = SavedDays.getAll()
    const remaining = days.filter(day => day.id !== id)
    if (remaining.length === days.length) {
      console.error('SavedDays.delete: day id=%o not found, nothing removed', id)
      return false
    }
    debugSavedPlans('SavedDays.delete: removed day id=%o', id)
    return SavedDays.saveAll(remaining)
  }

  static duplicate(id) {
    const days = SavedDays.getAll()
    const original = days.find(day => day.id === id)
    if (!original) {
      console.error('SavedDays.duplicate: day id=%o not found, nothing duplicated', id)
      return null
    }
    const copy = {
      ...JSON.parse(JSON.stringify(original)),
      id: SavedDays.nextId(days),
      name: `${original.name} (copy)`,
      createdAt: new Date().toISOString()
    }
    days.unshift(copy)
    if (!SavedDays.saveAll(days)) return null
    debugSavedPlans('SavedDays.duplicate: day id=%o copied as id=%o', id, copy.id)
    return copy.id
  }

  /**
   * Check a saved day before it reaches the store.
   *
   * Nothing is repaired silently: a day whose dives, gases, gradient factors or
   * environment cannot be trusted is refused as a whole. In particular an
   * unusable dive is NOT dropped the way Store.normalizeDay drops it - there,
   * truncating keeps the application usable; here, the diver asked for THIS
   * day, and giving him a shorter series without saying so would turn a
   * repetitive dive into a first dive of the day.
   *
   * Returns { valid: false, reason, errorKey } or
   * { valid: true, dives, gases, diluents, settings, environment,
   *   legacyEnvironment }.
   */
  static validateForLoad(day) {
    const dayId = day?.id
    const refuse = (reason, errorKey, detail) => {
      console.error('SavedDays.validateForLoad: day id=%o refused (%s): %s', dayId, reason, detail)
      return { valid: false, reason, errorKey }
    }

    if (!day || typeof day !== 'object' || Array.isArray(day)) {
      return refuse('day_not_an_object', PLAN_ERROR_CODES.INVALID_CONFIG, `value=${JSON.stringify(day)}`)
    }

    if (!Array.isArray(day.dives) || day.dives.length === 0) {
      return refuse('no_dives', PLAN_ERROR_CODES.NO_SEGMENTS, `dives=${JSON.stringify(day.dives)}`)
    }

    const dives = []
    for (let i = 0; i < day.dives.length; i++) {
      const dive = day.dives[i]
      if (!dive || typeof dive !== 'object' || Array.isArray(dive)) {
        return refuse('dive_not_an_object', PLAN_ERROR_CODES.NO_SEGMENTS, `dives[${i}]=${JSON.stringify(dive)}`)
      }
      if (!Array.isArray(dive.segments) || dive.segments.length === 0) {
        return refuse('dive_without_segment', PLAN_ERROR_CODES.NO_SEGMENTS,
          `dives[${i}].segments=${JSON.stringify(dive.segments)}`)
      }
      for (let s = 0; s < dive.segments.length; s++) {
        const segment = dive.segments[s]
        if (!segment || typeof segment !== 'object' || Array.isArray(segment)) {
          return refuse('segment_not_an_object', PLAN_ERROR_CODES.NO_SEGMENTS,
            `dives[${i}].segments[${s}]=${JSON.stringify(segment)}`)
        }
        if (typeof segment.depth !== 'number' || !Number.isFinite(segment.depth) || segment.depth < 0) {
          return refuse('segment_depth_unusable', PLAN_ERROR_CODES.INVALID_DEPTH,
            `dives[${i}].segments[${s}].depth=${JSON.stringify(segment.depth)}`)
        }
        if (typeof segment.time !== 'number' || !Number.isFinite(segment.time) || segment.time < 0) {
          return refuse('segment_time_unusable', PLAN_ERROR_CODES.INVALID_TIME,
            `dives[${i}].segments[${s}].time=${JSON.stringify(segment.time)}`)
        }
      }

      // The surface interval is what makes a dive a repetitive dive. The first
      // dive of the day follows nothing; every other one needs a real number,
      // never a value that would be read as zero (see
      // Store.isUsableSurfaceInterval).
      let surfaceIntervalBefore = null
      if (i > 0) {
        if (!Store.isUsableSurfaceInterval(dive.surfaceIntervalBefore)) {
          return refuse('surface_interval_unusable', PLAN_ERROR_CODES.INVALID_SURFACE_INTERVAL,
            `dives[${i}].surfaceIntervalBefore=${JSON.stringify(dive.surfaceIntervalBefore)}`)
        }
        surfaceIntervalBefore = dive.surfaceIntervalBefore
      } else if (dive.surfaceIntervalBefore !== null && dive.surfaceIntervalBefore !== undefined) {
        console.warn('SavedDays.validateForLoad: day id=%o, its first dive stores a %o min surface interval, which follows nothing and is dropped',
          dayId, dive.surfaceIntervalBefore)
      }

      dives.push({
        id: Number.isFinite(dive.id) ? dive.id : i + 1,
        label: typeof dive.label === 'string' ? dive.label : '',
        startTime: typeof dive.startTime === 'string' ? dive.startTime : '',
        surfaceIntervalBefore,
        segments: dive.segments,
        ccr: dive.ccr && typeof dive.ccr === 'object' && !Array.isArray(dive.ccr) ? dive.ccr : null,
        results: null,
        error: null,
        stale: false
      })
    }

    // The mixes the series was planned on, checked exactly as a saved plan
    // checks them: the dives point at these gases, and planning the same
    // depths on another list is how a diver ends up breathing a mix nobody
    // planned.
    const gases = SavedPlans.validateGasList(day.gases, 'gases', dayId)
    if (!gases) {
      return refuse('gases_unusable', PLAN_ERROR_CODES.INVALID_GAS, 'the day carries no usable gas list')
    }

    if (!day.settings || typeof day.settings !== 'object' || Array.isArray(day.settings)) {
      return refuse('settings_not_an_object', PLAN_ERROR_CODES.INVALID_CONFIG,
        `settings=${JSON.stringify(day.settings)}`)
    }
    const settings = { ...day.settings }
    for (const key of ['gfLow', 'gfHigh']) {
      const stored = settings[key]
      if (typeof stored !== 'number' || !Number.isFinite(stored) || stored <= 0) {
        return refuse(`${key}_unusable`, PLAN_ERROR_CODES.INVALID_GF, `settings.${key}=${JSON.stringify(stored)}`)
      }
      if (stored > 1) {
        // Same repair as Store.loadState and SavedPlans: some versions stored
        // the gradient factors as percentages.
        const asFraction = stored / 100
        if (asFraction > 1) {
          return refuse(`${key}_out_of_range`, PLAN_ERROR_CODES.INVALID_GF, `settings.${key}=${JSON.stringify(stored)}`)
        }
        console.warn('SavedDays.validateForLoad: day id=%o stores %s as the percentage %o, read as %o',
          dayId, key, stored, asFraction)
        settings[key] = asFraction
      }
    }

    // Diluents: needed as soon as one dive of the series is a rebreather dive.
    const needsDiluents = dives.some(dive => dive.ccr?.enabled === true)
    const diluents = SavedPlans.validateGasList(day.diluents, 'diluents', dayId)
    if (!diluents && needsDiluents) {
      return refuse('diluents_unusable', PLAN_ERROR_CODES.NO_DILUENT,
        'a day holding a rebreather dive cannot be loaded without the diluents it was planned with')
    }

    // Water and altitude, read exactly like a saved plan reads them
    const env = SavedPlans.readEnvironment(day)
    if (env.errorKey) {
      return refuse(env.reason, env.errorKey, 'the environment of the day cannot be read back')
    }

    return {
      valid: true,
      dives,
      gases,
      diluents,
      settings,
      environment: env.environment,
      legacyEnvironment: env.legacy
    }
  }

  /**
   * Load a saved day into the current state.
   *
   * The dive of the plan screen is left untouched: a day is a series of its
   * own, and overwriting the dive being planned would cost work nobody asked
   * to lose. The plan screen is however detached from the day it may have been
   * editing, since that day is no longer the one loaded.
   *
   * Returns { ok: true, day, legacyEnvironment } or
   * { ok: false, reason, errorKey }, every refusal logged and the store left
   * untouched.
   */
  static loadIntoStore(id, store) {
    const day = SavedDays.get(id)
    if (!day) {
      console.error('SavedDays.loadIntoStore: day id=%o not found, nothing loaded', id)
      return { ok: false, reason: 'day_not_found', errorKey: null }
    }

    const check = SavedDays.validateForLoad(day)
    if (!check.valid) {
      // validateForLoad logged the exact reason
      return { ok: false, reason: check.reason, errorKey: check.errorKey }
    }

    store.state.day = { dives: check.dives, editingIndex: null }
    store.state.gases = check.gases
    if (check.diluents) {
      store.state.diluents = check.diluents
    }
    store.state.settings = { ...store.state.settings, ...check.settings }
    store.state.environment = { ...store.state.environment, ...check.environment }

    // The plan on the plan screen was computed with the settings this load has
    // just replaced
    store.invalidateResults(Store.STALE_REASON_INPUTS, `the saved day "${day.name}"`)

    store.saveState()
    store.notify()

    if (check.legacyEnvironment) {
      console.warn('SavedDays.loadIntoStore: day id=%o was saved before the environment was part of a saved day, it is recomputed at sea level in sea water (%o)',
        id, SavedPlans.LEGACY_ENVIRONMENT)
    }
    debugSavedPlans('SavedDays.loadIntoStore: loaded day id=%o name="%s" (%d dives)', id, day.name, check.dives.length)
    return { ok: true, day, legacyEnvironment: check.legacyEnvironment }
  }

  static async exportToJson(id) {
    const day = SavedDays.get(id)
    if (!day) {
      console.error('SavedDays.exportToJson: day id=%o not found, nothing exported', id)
      return false
    }

    const exportData = {
      _generator: 'mvplan',
      _type: DAY_FILE_TYPE,
      _version: APP_VERSION,
      _exportedAt: new Date().toISOString(),
      ...day
    }
    const json = JSON.stringify(exportData, null, 2)
    const filename = `${String(day.name).replace(/[^a-z0-9]/gi, '_')}.json`

    if (isNativePlatform()) {
      try {
        const base64 = btoa(unescape(encodeURIComponent(json)))
        const result = await Filesystem.writeFile({
          path: filename,
          data: base64,
          directory: Directory.Cache
        })
        await Share.share({ title: filename, url: result.uri })
        return true
      } catch (e) {
        if (e.message?.includes('canceled') || e.message?.includes('cancelled')) {
          return true
        }
        console.error('SavedDays.exportToJson: native export failed:', e)
        return false
      }
    }

    try {
      const blob = new Blob([json], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      return true
    } catch (e) {
      console.error('SavedDays.exportToJson: export failed:', e)
      return false
    }
  }

  /**
   * Import a day from an exported file.
   *
   * The file has to say it is a day: a plan read as a day would come back as an
   * empty series, and the diver would only find out on the water. Everything
   * the engine will breathe is checked before the day enters the list, exactly
   * as a load checks it.
   *
   * Returns the id of the imported day, or null - the reason is always logged.
   */
  static importFromJson(jsonString) {
    let day
    try {
      day = JSON.parse(jsonString)
    } catch (e) {
      console.error('SavedDays.importFromJson: the file is not readable JSON:', e.message)
      return null
    }

    if (!day || typeof day !== 'object' || Array.isArray(day)) {
      console.error('SavedDays.importFromJson: the file does not hold an object (%o), nothing imported', day)
      return null
    }

    if (day._type !== DAY_FILE_TYPE) {
      console.error('SavedDays.importFromJson: the file is not a day of diving (_type=%o, expected %o), nothing imported',
        day._type, DAY_FILE_TYPE)
      return null
    }

    const check = SavedDays.validateForLoad(day)
    if (!check.valid) {
      // validateForLoad logged the exact reason
      console.error('SavedDays.importFromJson: the day cannot be loaded (%s), nothing imported', check.reason)
      return null
    }

    const days = SavedDays.getAll()
    // The export header is dropped instead of being stored: keeping it would
    // republish the version and the date of the machine the file came from the
    // next time this day is exported.
    const { _generator, _type, _version, _exportedAt, ...stored } = day
    stored.id = SavedDays.nextId(days)
    stored.createdAt = new Date().toISOString()
    stored.name = stored.name || 'Imported day'

    days.unshift(stored)
    if (!SavedDays.saveAll(days)) return null

    debugSavedPlans('SavedDays.importFromJson: imported day id=%d name="%s"', stored.id, stored.name)
    return stored.id
  }
}
